/**
 * outputChartSeries (#2506) — the SINGLE canonical description of what the
 * Ernteprognose chart draws: one entry per series, carrying its label, colour,
 * stroke, the ``OutputChartRow`` field it plots, and its toggle state.
 *
 * Before #2506 that description lived in three places inside
 * ``OutputSeriesChart.tsx`` — the ``<Line name=…>``s, the prior-year checkbox
 * block, and the selection-dot colour switch — with a passive recharts
 * ``<Legend />`` echoing a fourth copy at the bottom of the plot. Two legends
 * meant two label sets that could drift apart. Everything now derives from
 * ``buildChartSeries``, so a legend swatch cannot disagree with its line.
 *
 * Pure + view-agnostic: no React, no recharts, so it is directly unit-testable
 * (recharts renders nothing under jsdom).
 */
import type { ChartMode, OutputChartRow } from './outputSeries';

const IST_COLOR = '#2563eb'; // blue — actual
const PROGNOSE_COLOR = '#16a34a'; // green — forecast
const HORIZON_COLOR = '#d97706'; // amber — horizon overlay
const PLAN_COLOR = '#9ca3af'; // grey — plan target
/**
 * Thin/muted prior-year overlays — a progressively lighter slate ramp with −1
 * darkest ("the closer year reads stronger"). #2506 widened this from
 * slate-600/400/300 to slate-800/500/300: the old ramp left −2 and −3 only
 * ΔL* 18.4 apart, so at 1.5px stroke the dense June cluster read as one grey
 * smudge; the current ramp's smallest pairwise gap is ΔL* 31.9. Measured in CIE
 * L* rather than relative luminance — see the invariant in
 * ``__tests__/outputChartSeries.test.ts``. Blue stays reserved for Ist.
 */
const PRIOR_YEAR_COLORS: Record<number, string> = { 1: '#1e293b', 2: '#64748b', 3: '#cbd5e1' };

export { HORIZON_COLOR, IST_COLOR, PLAN_COLOR, PRIOR_YEAR_COLORS, PROGNOSE_COLOR };

/** One available prior-year overlay + its current toggle state. */
export interface PriorYearToggle {
  offset: number;
  label: string;
  enabled: boolean;
}

/** The CUMULATIVE ``OutputChartRow`` fields — a series' running season total. */
export type ChartSeriesCumulativeKey =
  | 'actualCum'
  | 'forecastCum'
  | 'horizonCum'
  | 'planTarget'
  | 'priorYear1'
  | 'priorYear2'
  | 'priorYear3';

/** The PER-BUCKET ``OutputChartRow`` fields (#2544-B). No ``planBucket``: the
 *  plan is a season scalar, so the Plan series has no Einzelernte counterpart. */
export type ChartSeriesBucketKey =
  | 'actualBucket'
  | 'forecastBucket'
  | 'horizonBucket'
  | 'priorYear1Bucket'
  | 'priorYear2Bucket'
  | 'priorYear3Bucket';

/** The ``OutputChartRow`` fields a series can plot, in either data mode. */
export type ChartSeriesDataKey = ChartSeriesCumulativeKey | ChartSeriesBucketKey;

/**
 * What a series' end value MEANS — so the legend's number cannot be misread.
 * Ist stops at Heute; Prognose/horizon run to the season end; Plan is a flat
 * target; a prior year is a closed season's total.
 */
export type ChartValueKind = 'today' | 'expected-final' | 'target' | 'season-total';

/** German copy for each value kind, shown beside the legend's number. */
export const VALUE_KIND_LABEL: Record<ChartValueKind, string> = {
  today: 'Stand heute',
  'expected-final': 'erwartetes Saisonende',
  target: 'Saisonziel',
  'season-total': 'Saisonsumme',
};

/**
 * How a series is DRAWN: a ``<Line>`` or a ``<Bar>``. Ist becomes a column when
 * the chart is presented as Säulen, and nothing else ever does (four bar series
 * would be unreadable).
 *
 * It is a separate field rather than an ``if (mode === …)`` at the render site so
 * that #2546's user-facing ``Linie | Säulen`` toggle stays a pure override of a
 * default rather than a second place deciding whether Ist is a column. #2544-B
 * introduced it as a mode-derived internal default with no URL param; #2546-B
 * put the toggle and ``?pm=`` on top without moving the decision.
 */
export type ChartSeriesMark = 'line' | 'bar';

/**
 * The PRESENTATION axis (#2546-B) — how the numbers are drawn, orthogonal to the
 * {@link ChartMode} data axis that says what they mean. The two are genuinely
 * independent: all four combinations are valid and reachable.
 *
 * | Data mode  | Presentation | |
 * |------------|--------------|--|
 * | Kumuliert  | Linie        | the original chart |
 * | Kumuliert  | Säulen       | cumulative staircase |
 * | Einzelernte| Säulen       | per-bucket amounts as columns |
 * | Einzelernte| Linie        | rhythm tracing |
 *
 * ASCII ``saeulen`` rather than ``säulen``: these are the ``?pm=`` URL tokens,
 * and the repo folds umlauts out of URL values by habit.
 */
export type ChartPresentation = 'linie' | 'saeulen';

/**
 * ``?pm=`` → the presentation, resolved against the active data mode.
 *
 * **TRI-STATE, and this is the subtle part of #2546-B.** The DEFAULT presentation
 * is data-mode dependent — Kumuliert shows lines, Einzelernte shows columns —
 * so the state has to distinguish three things, not two:
 *
 *   • no explicit choice (absent/unknown) → follow the mode, and RE-DERIVE when
 *     the mode switches;
 *   • explicit ``linie``   → stay Linie even after switching to Einzelernte;
 *   • explicit ``saeulen`` → stay Säulen even after switching to Kumuliert.
 *
 * A two-value resolver (``param === 'saeulen'``) cannot express the first case:
 * it would read an absent param as "Linie, explicitly" and so break the
 * "Einzelernte defaults to Säulen" rule on every mode switch. The third state
 * lives in the URL as the ABSENCE of ``?pm=``, which is why the toggle writes
 * both values explicitly instead of clearing the default the way ``?m=`` does.
 *
 * An unknown token is treated as "no choice" rather than as an error, matching
 * ``resolveGrain`` / ``resolveChartMode``: a hand-edited or stale URL renders the
 * default view, never a blank chart.
 */
export function resolvePresentation(param: string | null, mode: ChartMode): ChartPresentation {
  if (param === 'linie' || param === 'saeulen') return param;
  return mode === 'perPeriod' ? 'saeulen' : 'linie';
}

export interface ChartSeries {
  /** Stable identity; matches the "Ausgewählter Tag" readout's line keys. */
  key: string;
  /** The row field this series PLOTS in the active data mode. */
  dataKey: ChartSeriesDataKey;
  /**
   * The row field carrying this series' running season total — mode-INDEPENDENT.
   * ``seriesEndValue`` reads this, never ``dataKey``, so the legend KPI stays the
   * honest "Stand heute" / "Saisonsumme" in Einzelernte too (where ``dataKey``
   * holds a single bucket's harvest).
   */
  cumulativeKey: ChartSeriesCumulativeKey;
  label: string;
  color: string;
  strokeWidth: number;
  strokeDasharray?: string;
  connectNulls: boolean;
  /** Line or column. Presentation-derived; see {@link ChartSeriesMark}. */
  mark: ChartSeriesMark;
  /** The prior-year offset for a toggleable overlay; ``null`` for the primary series. */
  offset: number | null;
  /** Whether the series is drawn. Primary series are always on. */
  enabled: boolean;
  valueKind: ChartValueKind;
}

export interface BuildChartSeriesOptions {
  showHorizon: boolean;
  horizonDays: number;
  hasPlan: boolean;
  priorYears: PriorYearToggle[];
  /** Which numbers to plot: running totals (``cumulative``) or per-bucket
   *  amounts (``perPeriod``). Required — every call site states its intent. */
  mode: ChartMode;
  /**
   * How to draw them (#2546-B). Optional, and deliberately so: omitting it means
   * "the default for this mode", which is exactly ``resolvePresentation(null,
   * mode)``. Call sites that do not care how the chart is drawn — the
   * "Ausgewählter Tag" readout builds the same series list purely to LABEL
   * numbers — therefore keep working unchanged and cannot pick a wrong value.
   */
  presentation?: ChartPresentation;
}

const PRIOR_YEAR_DATA_KEYS: Record<number, ChartSeriesCumulativeKey> = {
  1: 'priorYear1',
  2: 'priorYear2',
  3: 'priorYear3',
};

const PRIOR_YEAR_BUCKET_KEYS: Record<number, ChartSeriesBucketKey> = {
  1: 'priorYear1Bucket',
  2: 'priorYear2Bucket',
  3: 'priorYear3Bucket',
};

/**
 * The canonical series list in READING order — Ist, Prognose, [horizon], [plan],
 * then each available prior year. This is the order the legend renders and the
 * order the "Ausgewählter Tag" readout already uses; ``plotOrder`` re-sorts it
 * for recharts' source-order-is-z-order rule.
 *
 * Toggled-off prior years ARE included (``enabled: false``) — they are the
 * control that re-enables them. Only ``plotOrder`` drops them.
 *
 * #2544-B — ``mode`` picks which row field each series plots. Everything else
 * (label, colour, stroke, value kind, ordering) is mode-INDEPENDENT, so
 * Kumuliert is byte-identical to the pre-mode chart. One mode-specific rule
 * remains here: the Plan series is omitted in ``perPeriod`` (a season scalar has
 * no per-bucket value).
 *
 * #2546-B — whether Ist is a column is now the PRESENTATION's call, not the
 * mode's. ``presentation`` is the only thing ``mark`` reads, and ``mark`` is the
 * only thing ``presentation`` writes: the two axes stay orthogonal, so every
 * cell of the 2×2 matrix is reachable and a data-mode switch cannot silently
 * repoint a series at a different row field.
 */
export function buildChartSeries({
  showHorizon,
  horizonDays,
  hasPlan,
  priorYears,
  mode,
  presentation = resolvePresentation(null, mode),
}: BuildChartSeriesOptions): ChartSeries[] {
  const perPeriod = mode === 'perPeriod';
  const saeulen = presentation === 'saeulen';
  const series: ChartSeries[] = [
    {
      key: 'ist',
      dataKey: perPeriod ? 'actualBucket' : 'actualCum',
      cumulativeKey: 'actualCum',
      label: 'Ist',
      color: IST_COLOR,
      strokeWidth: 3,
      connectNulls: false,
      // The ONE bar in the chart, in every cell of the matrix. Prognose and the
      // prior years stay lines whatever the presentation, so they remain legible
      // over/behind the columns — four bar series would be unreadable.
      mark: saeulen ? 'bar' : 'line',
      offset: null,
      enabled: true,
      valueKind: 'today',
    },
    {
      key: 'prognose',
      dataKey: perPeriod ? 'forecastBucket' : 'forecastCum',
      cumulativeKey: 'forecastCum',
      label: 'Prognose',
      color: PROGNOSE_COLOR,
      strokeDasharray: '6 4',
      strokeWidth: 2,
      connectNulls: false,
      mark: 'line',
      offset: null,
      enabled: true,
      valueKind: 'expected-final',
    },
  ];
  if (showHorizon) {
    series.push({
      key: 'horizon',
      dataKey: perPeriod ? 'horizonBucket' : 'horizonCum',
      cumulativeKey: 'horizonCum',
      label: `Prognose vor ${horizonDays} Tagen`,
      color: HORIZON_COLOR,
      strokeDasharray: '2 3',
      strokeWidth: 2,
      connectNulls: false,
      mark: 'line',
      offset: null,
      enabled: true,
      valueKind: 'expected-final',
    });
  }
  // #2544-B — Plan is a seasonal SCALAR (``OutputPlanTarget`` is one number for
  // the whole plan period, and ``yield_plan`` stores a target, not a curve), so
  // it has no per-bucket counterpart to draw. Hidden in Einzelernte rather than
  // spread across buckets, which would invent a plan shape nobody entered.
  if (hasPlan && !perPeriod) {
    series.push({
      key: 'plan',
      dataKey: 'planTarget',
      cumulativeKey: 'planTarget',
      label: 'Plan (Ziel)',
      color: PLAN_COLOR,
      strokeDasharray: '4 4',
      strokeWidth: 2,
      connectNulls: true,
      mark: 'line',
      offset: null,
      enabled: true,
      valueKind: 'target',
    });
  }
  for (const py of priorYears) {
    const cumulativeKey = PRIOR_YEAR_DATA_KEYS[py.offset];
    const bucketKey = PRIOR_YEAR_BUCKET_KEYS[py.offset];
    // The overlay supports −1/−2/−3 only (priorYearOverlay's MAX_OFFSET); an
    // unknown offset is dropped rather than silently plotted as another year.
    // BOTH maps are checked: they must stay key-synchronised, and a hole in only
    // the bucket map would otherwise reach recharts as ``dataKey: undefined``
    // with a perfectly green typecheck.
    if (!cumulativeKey || !bucketKey) continue;
    series.push({
      key: `py-${py.offset}`,
      dataKey: perPeriod ? bucketKey : cumulativeKey,
      cumulativeKey,
      label: py.label,
      color: PRIOR_YEAR_COLORS[py.offset],
      strokeWidth: 1.5,
      connectNulls: false,
      mark: 'line',
      offset: py.offset,
      enabled: py.enabled,
      valueKind: 'season-total',
    });
  }
  return series;
}

/**
 * The DRAWN series in recharts source order, which is z-order: prior-year
 * overlays first so they sit UNDER the primary lines. Toggled-off overlays drop.
 */
export function plotOrder(series: ChartSeries[]): ChartSeries[] {
  const drawn = series.filter((s) => s.enabled);
  return [...drawn.filter((s) => s.offset !== null), ...drawn.filter((s) => s.offset === null)];
}

/**
 * A series' end value — its LAST non-null cumulative across ``rows``, i.e. the
 * KPI the legend shows. Deliberately scans the FULL row range, not the zoom
 * window, so a "Saisonsumme" does not change as the user pans; and the REAL rows,
 * not ``withForecastJoin``'s bridged copy, so the synthetic joining point can
 * never surface as a displayed number. Trailing nulls are skipped, so Ist still
 * reports its own last real value while the Prognose spine runs past Heute.
 *
 * #2544-B — reads ``cumulativeKey``, NOT ``dataKey``. In Einzelernte ``dataKey``
 * holds one bucket's harvest, so a KPI read off it would render "Stand heute:
 * 15 kg" for a season that has produced tonnes. The number beside a legend entry
 * means the same thing in both modes.
 */
export function seriesEndValue(rows: OutputChartRow[], series: ChartSeries): number | null {
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    const v = rows[i][series.cumulativeKey];
    if (v != null) return v;
  }
  return null;
}
